import { useEffect, useRef, type MutableRef } from 'preact/hooks';

// A sideways swipe on a phone (Design's phone handoff: |dx| > 60 px and |dx| > 1.5 |dy|): the grid
// pages with it, and the item and member pages step to the next or the previous one. A swipe that
// starts at the screen's edge belongs to the phone (its own Back), none counts while the view is
// zoomed in (a sideways swipe then moves the view), and none that starts in something that scrolls
// sideways of its own (a wide table).
const EDGE = 24;
const SWIPE = 60;

const width = () => document.documentElement.clientWidth || window.innerWidth;

function scrollsSideways(from: EventTarget | null, root: HTMLElement): boolean {
  for (let el = from as HTMLElement | null; el && el !== root; el = el.parentElement) {
    if (el.scrollWidth > el.clientWidth + 1 && /auto|scroll/.test(getComputedStyle(el).overflowX)) return true;
  }
  return false;
}

/** `onSwipe(1)`: a swipe to the left (the next: older, or the next person); `onSwipe(-1)`: to the right. */
export function useSwipe(ref: MutableRef<HTMLElement | null>, onSwipe: (dir: 1 | -1) => void) {
  const go = useRef(onSwipe);
  go.current = onSwipe;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let start: { x: number; y: number } | null = null;
    const down = (e: TouchEvent) => {
      const t = e.touches[0];
      const zoomed = (window.visualViewport?.scale ?? 1) > 1.01;
      start = e.touches.length === 1 && !zoomed && t.clientX > EDGE && t.clientX < width() - EDGE
        && !scrollsSideways(e.target, el) ? { x: t.clientX, y: t.clientY } : null;
    };
    const up = (e: TouchEvent) => {
      if (!start) return;
      const t = e.changedTouches[0], dx = t.clientX - start.x, dy = t.clientY - start.y;
      start = null;
      if (Math.abs(dx) <= SWIPE || Math.abs(dx) <= 1.5 * Math.abs(dy)) return;
      go.current(dx < 0 ? 1 : -1);
    };
    el.addEventListener('touchstart', down, { passive: true });
    el.addEventListener('touchend', up, { passive: true });
    return () => { el.removeEventListener('touchstart', down); el.removeEventListener('touchend', up); };
  }, []);
}
